import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Dns as DnsIcon, WarningAmber as WarningIcon } from '@mui/icons-material';

/**
 * Which domain controller the data ON THIS PAGE came from.
 *
 * This is deliberately NOT the toolbar selector. The selector says what you
 * asked for; this says what actually answered. They can differ -- a request
 * with no selection falls back to the default agent, and a listing can be
 * served while a different DC is selected. Two domains legitimately contain the
 * same names ("Administrator", "Domain Admins"), so a directory listing with no
 * provenance is genuinely unsafe to act on: you cannot tell rrs.internal from
 * testlab.internal without reading a DN and knowing what to look for.
 *
 * `source` is the block every listing endpoint returns (see
 * apps/core/pagination.py). Renders nothing when absent, so a page that has not
 * loaded yet shows no misleading label.
 */
function DataSource({ source, sx }) {
  if (!source || !source.agent) return null;

  const { agent, realm, base_dn: baseDn, hostname, status, explicit } = source;
  const offline = status && status !== 'online';

  const detail = [
    realm && `realm ${realm}`,
    baseDn && `base ${baseDn}`,
    hostname && `host ${hostname}`,
    status && `status ${status}`,
    explicit ? 'explicitly selected' : 'default agent (no domain was specified)',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ...sx }}>
      <Typography variant="body2" color="text.secondary">
        Reading from
      </Typography>
      <Tooltip title={detail} arrow>
        <Chip
          size="small"
          icon={offline ? <WarningIcon /> : <DnsIcon />}
          color={offline ? 'warning' : 'default'}
          variant={explicit ? 'filled' : 'outlined'}
          label={realm ? `${agent} — ${realm}` : agent}
        />
      </Tooltip>
      {!explicit && (
        <Typography variant="caption" color="text.secondary">
          (default domain)
        </Typography>
      )}
      {offline && (
        <Typography variant="caption" color="warning.main">
          agent is {status} — this data may be stale
        </Typography>
      )}
    </Box>
  );
}

export default DataSource;
